import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const CRITERES = [
  { key: 'assiduite',      label: 'Assiduité et ponctualité' },
  { key: 'integration',    label: 'Intégration dans l\'équipe' },
  { key: 'initiative',     label: 'Initiative et autonomie' },
  { key: 'tenue',          label: 'Tenue professionnelle' },
  { key: 'qualite',        label: 'Qualité du travail fourni' },
  { key: 'consignes',      label: 'Respect des consignes' },
  { key: 'communication',  label: 'Communication' },
];

const NIVEAUX = [
  { value: 1, label: 'Très insuffisant' },
  { value: 2, label: 'Insuffisant' },
  { value: 3, label: 'Satisfaisant' },
  { value: 4, label: 'Très satisfaisant' },
];

const NIVEAU_COLORS = {
  1: 'bg-red-100 text-red-700 border-red-300',
  2: 'bg-orange-100 text-orange-700 border-orange-300',
  3: 'bg-blue-100 text-blue-700 border-blue-300',
  4: 'bg-green-100 text-green-700 border-green-300',
};

const NIVEAU_SELECTED = {
  1: 'bg-red-500 text-white border-red-500',
  2: 'bg-orange-500 text-white border-orange-500',
  3: 'bg-blue-500 text-white border-blue-500',
  4: 'bg-green-500 text-white border-green-500',
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '';

export default function AppreciationForm({ convention, onSaved, locked = false, unlockDate = null }) {
  const already = !!convention.appreciation;

  const [criteres, setCriteres] = useState({});
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (convention.appreciation) {
      const a = convention.appreciation;
      setCriteres(a.criteresEvaluation || {});
      setComment(a.comment || '');
    }
  }, [convention]);

  const setCritere = (key, value) =>
    setCriteres(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await api.post('/appreciations', {
        conventionId: convention.id,
        comment,
        criteresEvaluation: criteres,
        // Préserver les champs déjà enregistrés
        stagiairePresent: convention.appreciation?.stagiairePresent ?? null,
        disponibilites: convention.appreciation?.disponibilites || '',
        tutorContactPreference: convention.tutorContactPreference || null,
      });
      setSuccess(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h3 className="font-semibold text-gray-800 text-base">
        {already ? 'Modifier mon appréciation' : 'Saisir mon appréciation de stage'}
      </h3>

      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          Cette section sera disponible à partir du <strong>{fmtDate(unlockDate)}</strong> (70 % du stage écoulé). Vous pouvez dès maintenant prendre connaissance des critères d'évaluation.
        </div>
      )}

      {/* Matrice d'évaluation */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Évaluation par critères</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 pb-2 pr-4 w-1/2">Critère</th>
                {NIVEAUX.map(n => (
                  <th key={n.value} className="text-center text-xs font-medium text-gray-500 pb-2 px-1 min-w-[90px]">
                    {n.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {CRITERES.map(({ key, label }) => (
                <tr key={key} className="hover:bg-gray-50">
                  <td className="py-2.5 pr-4 text-gray-700 text-sm">{label}</td>
                  {NIVEAUX.map(n => {
                    const selected = criteres[key] === n.value;
                    return (
                      <td key={n.value} className="py-2.5 px-1 text-center">
                        <button
                          type="button"
                          onClick={() => !locked && setCritere(key, n.value)}
                          disabled={locked}
                          className={`w-full px-2 py-1.5 rounded border text-xs font-medium transition-colors ${
                            locked ? 'opacity-40 cursor-not-allowed' : ''
                          } ${selected ? NIVEAU_SELECTED[n.value] : `${NIVEAU_COLORS[n.value]} hover:opacity-80`}`}
                        >
                          {selected ? '✓' : '—'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-3 mt-2 flex-wrap">
          {NIVEAUX.map(n => (
            <span key={n.value} className={`text-xs px-2 py-0.5 rounded border ${NIVEAU_COLORS[n.value]}`}>
              {n.label}
            </span>
          ))}
        </div>
      </div>

      {/* Commentaire libre */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Appréciation générale *</label>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          required
          disabled={locked}
          rows={5}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${locked ? 'opacity-40 cursor-not-allowed bg-gray-50' : ''}`}
          placeholder="Décrivez le déroulement du stage, les points forts et axes d'amélioration de l'étudiant(e)..."
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          Appréciation enregistrée avec succès.
        </div>
      )}

      <button
        type="submit"
        disabled={saving || locked}
        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
      >
        {saving ? 'Enregistrement...' : already ? 'Mettre à jour' : 'Enregistrer l\'appréciation'}
      </button>
    </form>
  );
}
